import { BackgroundTypes, MapModes, ReactMap } from '@innovyze/inno-map';
import {
	useAuth0,
	useGetMapBackground,
	useIsFeatureEnabled,
} from '@innovyze/stylovyze';

import { getSpatialLayers } from '@Actions/spatial.actions';
import { useReactMapConfig } from '@innovyze/stylovyze';
import { useSelectMapInsertSpatial } from '@Selectors';
import { useFeatureFlagBridge } from '@Utils';
import { MapStatusType } from '@Types';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

export interface MapInsertSpatialProps {
	layerName: string;
	showSystemTypeLayers?: boolean;
	logLevel?: MapStatusType;
	/**
	 * Zoom-in levels for polygon-like layers (Polygon / MultiPolygon), which
	 * otherwise render too far out to read. Implemented by fitting the map to a
	 * box shrunk around the layer extent (1/2^boost), so boost ≈ pressing "+"
	 * that many times from the plain fit-to-extent view. No effect on
	 * point/line layers. Leave unset for the default extent-based framing.
	 */
	polygonZoomBoost?: number;
}

const MapInsertSpatialComponent: React.FC<MapInsertSpatialProps> = ({
	layerName,
	showSystemTypeLayers = false,
	logLevel = MapStatusType.WARN,
	polygonZoomBoost,
}: MapInsertSpatialProps) => {
	const stylovyzeDefaultBackground = useGetMapBackground() as BackgroundTypes;

	const { token } = useAuth0();
	const featureFlagBridge = useFeatureFlagBridge();
	const isCloudOsTilesetEnabled = !!useIsFeatureEnabled(
		'i-360-tileset-read-from-cloudos',
	);

	const dispatch = useDispatch();
	const inputs = useSelectMapInsertSpatial(
		token,
		layerName,
		showSystemTypeLayers,
		isCloudOsTilesetEnabled,
	);

	useEffect(() => {
		if (!inputs?.layers) {
			dispatch(getSpatialLayers());
		}
	}, [dispatch, inputs, layerName]);

	const background = stylovyzeDefaultBackground ?? BackgroundTypes.OSMStreets;

	const reactMapConfig = useReactMapConfig();

	const position = inputs.location?.position;
	const extent = inputs.location?.extent;
	const isPolygonLike =
		inputs.layers?.some(layer => layer.type === 'polygon') ?? false;
	const boost = polygonZoomBoost ?? 0;

	// Frame polygon layers by fitting the map to a box shrunk around the layer
	// extent. Halving the box's linear size zooms in ~1 level, so a 1/2^boost
	// factor ≈ pressing "+" `boost` times from the plain fit-to-extent view.
	// defaultBounds is used (not position) because Esri applies it reactively via
	// its own fit math — `position` is only read once at map init, so it can't
	// react to the asynchronously-loaded layer extent.
	const extentValid =
		!!extent && extent.length === 4 && extent.some(value => value !== 0);
	const shouldFrame = boost > 0 && isPolygonLike && extentValid;

	let defaultBounds: number[] | undefined;
	if (shouldFrame && extent) {
		const factor = 1 / Math.pow(2, boost);
		const centerX = (extent[0] + extent[2]) / 2;
		const centerY = (extent[1] + extent[3]) / 2;
		const halfWidth = ((extent[2] - extent[0]) / 2) * factor;
		const halfHeight = ((extent[3] - extent[1]) / 2) * factor;
		defaultBounds = [
			centerX - halfWidth,
			centerY - halfHeight,
			centerX + halfWidth,
			centerY + halfHeight,
		];
	}

	return (
		<ReactMap
			featureFlagBridge={featureFlagBridge}
			config={reactMapConfig}
			mode={MapModes.Navigation}
			dataServices={inputs.sources}
			layerConfig={inputs.layers}
			autoResize={false}
			position={shouldFrame ? undefined : position ?? undefined}
			defaultBounds={defaultBounds}
			hiddenLayers={inputs.hiddenLayers}
			showMinZoomWarning={false}
			logLevel={logLevel ?? MapStatusType.WARN}
			background={background}
		/>
	);
};
export default MapInsertSpatialComponent;
